import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  IconButton,
  Alert,
  CircularProgress,
  Button,
  Tooltip,
  Popover,
  MenuItem,
  keyframes,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import {
  useNiboSuppliers,
  useNiboExpenseCategories,
  useNiboCostCenters,
  useNiboFinancialAccounts,
  useNiboEntryData,
  useUpsertNiboEntryData,
  useCreateNiboSupplier,
  niboKeys,
} from '@/hooks/useNibo';
import type {
  Supplier,
  Category,
  CostCenter,
  FinancialAccount,
} from '@/services/nibo/nibo.types';
import type { NiboData } from '@/components/entry-form/types';
import { isValidCNPJ, isValidCPF } from '@/utils/validators';
import { formatCNPJ, stripCNPJ } from '@/utils/cnpj';
import { useNotification } from '@/contexts/NotificationContext';
import { AxiosError } from 'axios';
import { formatCostCenterLabel, getCostCenterSnapshot } from '@/components/entry-form/costCenterLabel';

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

export interface NiboFieldErrors {
  supplierId?: string;
  categoryId?: string;
  costCenterId?: string;
  financialAccountId?: string;
}

interface NiboEntryDataFormProps {
  entryId?: string;
  customerId: string;
  hasIntegration: boolean;
  supplierDocument?: string;
  initialData?: NiboData;
  onChange?: (data: NiboData) => void;
  errors?: NiboFieldErrors;
}

export const NiboEntryDataForm: React.FC<NiboEntryDataFormProps> = ({
  entryId,
  customerId,
  hasIntegration,
  supplierDocument,
  initialData,
  onChange,
  errors,
}) => {
  const queryClient = useQueryClient();
  const isCreateMode = !entryId;

  const { data: entryData, isLoading: isLoadingEntryData } = useNiboEntryData(
    entryId || '',
    hasIntegration && !isCreateMode,
  );
  const suppliersQuery = useNiboSuppliers(customerId, hasIntegration);
  const categoriesQuery = useNiboExpenseCategories(customerId, hasIntegration);
  const costCentersQuery = useNiboCostCenters(customerId, hasIntegration);
  const accountsQuery = useNiboFinancialAccounts(customerId, hasIntegration);

  const suppliers = suppliersQuery.data ?? [];
  const categories = categoriesQuery.data ?? [];
  const costCenters = costCentersQuery.data ?? [];
  const financialAccounts = accountsQuery.data ?? [];

  const isLoadingSuppliers = suppliersQuery.isLoading;
  const isLoadingCategories = categoriesQuery.isLoading;
  const isLoadingCostCenters = costCentersQuery.isLoading;
  const isLoadingAccounts = accountsQuery.isLoading;

  const isFetchingSuppliers = suppliersQuery.isFetching;
  const isFetchingCategories = categoriesQuery.isFetching;
  const isFetchingCostCenters = costCentersQuery.isFetching;
  const isFetchingAccounts = accountsQuery.isFetching;

  const upsertMutation = useUpsertNiboEntryData();
  const createSupplierMutation = useCreateNiboSupplier();
  const { showNotification } = useNotification();

  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [selectedCostCenter, setSelectedCostCenter] = useState<CostCenter | null>(null);
  const [selectedAccount, setSelectedAccount] = useState<FinancialAccount | null>(null);
  const [showCreateSupplier, setShowCreateSupplier] = useState(false);
  const supplierBtnRef = useRef<HTMLButtonElement>(null);
  const supplierPopoverRef = useRef<HTMLDivElement>(null);
  const [newSupplierName, setNewSupplierName] = useState('');
  const [newSupplierDocument, setNewSupplierDocument] = useState('');
  const [newSupplierType, setNewSupplierType] = useState<'Jurídica' | 'Física'>('Jurídica');
  const [documentError, setDocumentError] = useState('');

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!showCreateSupplier) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (supplierPopoverRef.current?.contains(target)) return;
      if (supplierBtnRef.current?.contains(target)) return;
      if (target.closest('[role="listbox"]') || target.closest('[role="option"]') || target.closest('.MuiAutocomplete-popper')) return;
      setShowCreateSupplier(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCreateSupplier]);

  const initialSyncDoneRef = useRef(false);

  useEffect(() => {
    if (!entryData || initialSyncDoneRef.current) return;

    initialSyncDoneRef.current = true;
    onChangeRef.current?.({
      supplierId: entryData.supplierId || undefined,
      supplierName: entryData.supplierName || undefined,
      supplierDocument: entryData.supplierDocument || undefined,
      categoryId: entryData.categoryId || undefined,
      costCenterId: entryData.costCenterId || undefined,
      costCenterName: entryData.costCenterName || undefined,
      costCenterCode: entryData.costCenterCode || undefined,
      financialAccountId: entryData.financialAccountId || undefined,
    });
  }, [entryData]);

  useEffect(() => {
    if (!entryData) return;

    if (entryData.supplierId && suppliers.length > 0) {
      const found = suppliers.find((s) => s.id === entryData.supplierId);
      if (found) setSelectedSupplier(found);
    }
    if (entryData.categoryId && categories.length > 0) {
      const found = categories.find((c) => c.id === entryData.categoryId);
      if (found) setSelectedCategory(found);
    }
    if (entryData.costCenterId && costCenters.length > 0) {
      const found = costCenters.find((c) => c.id === entryData.costCenterId);
      if (found) setSelectedCostCenter(found);
    }
    if (entryData.financialAccountId && financialAccounts.length > 0) {
      const found = financialAccounts.find((a) => a.id === entryData.financialAccountId);
      if (found) setSelectedAccount(found);
    }
  }, [entryData, suppliers, categories, costCenters, financialAccounts]);

  const initialDataSyncDoneRef = useRef(false);
  useEffect(() => {
    if (!isCreateMode || !initialData || initialDataSyncDoneRef.current) return;

    const allLoaded = suppliers.length > 0 || categories.length > 0 || costCenters.length > 0 || financialAccounts.length > 0;
    if (!allLoaded) return;

    initialDataSyncDoneRef.current = true;

    if (initialData.supplierId && suppliers.length > 0) {
      const found = suppliers.find((s) => s.id === initialData.supplierId);
      if (found) setSelectedSupplier(found);
    }
    if (initialData.categoryId && categories.length > 0) {
      const found = categories.find((c) => c.id === initialData.categoryId);
      if (found) setSelectedCategory(found);
    }
    if (initialData.costCenterId && costCenters.length > 0) {
      const found = costCenters.find((c) => c.id === initialData.costCenterId);
      if (found) setSelectedCostCenter(found);
    }
    if (initialData.financialAccountId && financialAccounts.length > 0) {
      const found = financialAccounts.find((a) => a.id === initialData.financialAccountId);
      if (found) setSelectedAccount(found);
    }

    onChangeRef.current?.(initialData);
  }, [isCreateMode, initialData, suppliers, categories, costCenters, financialAccounts]);

  useEffect(() => {
    if (supplierDocument && suppliers.length > 0 && !selectedSupplier && !entryData?.supplierId && !initialData?.supplierId) {
      const cleanDoc = stripCNPJ(supplierDocument);
      const found = suppliers.find(
        (s) => s.documentNumber ? stripCNPJ(s.documentNumber) === cleanDoc : false
      );
      if (found) {
        setSelectedSupplier(found);
        handleSave('supplierId', found.id, found.name, supplierDocument);
      }
    }
  }, [supplierDocument, suppliers, selectedSupplier, entryData]);

  const handleSave = (
    field: string,
    value: string | undefined,
    supplierName?: string,
    supplierDoc?: string,
    costCenterSnapshot?: ReturnType<typeof getCostCenterSnapshot>
  ) => {
    const selectedCostCenterSnapshot = selectedCostCenter
      ? getCostCenterSnapshot(selectedCostCenter)
      : {};

    if (isCreateMode) {
      const data: NiboData = {
        supplierId: field === 'supplierId' ? value : selectedSupplier?.id,
        supplierName: field === 'supplierId' ? supplierName : selectedSupplier?.name,
        supplierDocument: field === 'supplierId' ? supplierDoc : (selectedSupplier?.documentNumber || undefined),
        categoryId: field === 'categoryId' ? value : selectedCategory?.id,
        costCenterId: field === 'costCenterId' ? value : selectedCostCenter?.id,
        costCenterName: field === 'costCenterId'
          ? costCenterSnapshot?.costCenterName
          : selectedCostCenterSnapshot.costCenterName,
        costCenterCode: field === 'costCenterId'
          ? costCenterSnapshot?.costCenterCode
          : selectedCostCenterSnapshot.costCenterCode,
        financialAccountId: field === 'financialAccountId' ? value : selectedAccount?.id,
      };
      onChange?.(data);
    } else {
      const apiData: Record<string, string | undefined> = { [field]: value };
      if (field === 'supplierId') {
        apiData.supplierName = supplierName;
        apiData.supplierDocument = supplierDoc;
      }
      if (field === 'costCenterId') {
        apiData.costCenterName = costCenterSnapshot?.costCenterName;
        apiData.costCenterCode = costCenterSnapshot?.costCenterCode;
      }
      upsertMutation.mutate({ entryId: entryId!, data: apiData });

      const updatedSupplier = field === 'supplierId' ? value : selectedSupplier?.id;
      const updatedCategory = field === 'categoryId' ? value : selectedCategory?.id;
      const updatedCostCenter = field === 'costCenterId' ? value : selectedCostCenter?.id;
      const updatedAccount = field === 'financialAccountId' ? value : selectedAccount?.id;
      onChange?.({
        supplierId: updatedSupplier,
        supplierName: field === 'supplierId' ? supplierName : selectedSupplier?.name,
        supplierDocument: field === 'supplierId' ? supplierDoc : (selectedSupplier?.documentNumber || undefined),
        categoryId: updatedCategory,
        costCenterId: updatedCostCenter,
        costCenterName: field === 'costCenterId'
          ? costCenterSnapshot?.costCenterName
          : selectedCostCenterSnapshot.costCenterName,
        costCenterCode: field === 'costCenterId'
          ? costCenterSnapshot?.costCenterCode
          : selectedCostCenterSnapshot.costCenterCode,
        financialAccountId: updatedAccount,
      });
    }
  };

  const handleCreateSupplier = () => {
    if (!newSupplierName || !newSupplierDocument) return;

    if (newSupplierType === 'Jurídica' && !isValidCNPJ(newSupplierDocument)) {
      setDocumentError('CNPJ inválido');
      return;
    }

    if (newSupplierType === 'Física' && !isValidCPF(newSupplierDocument)) {
      setDocumentError('CPF inválido');
      return;
    }

    setDocumentError('');
    const documentNumber = newSupplierType === 'Jurídica'
      ? stripCNPJ(newSupplierDocument)
      : newSupplierDocument.replace(/\D/g, '');
    createSupplierMutation.mutate(
      {
        customerId,
        tipoPessoa: newSupplierType,
        nome: newSupplierName,
        documento: documentNumber,
      },
      {
        onSuccess: (newSupplier) => {
          setSelectedSupplier(newSupplier);
          handleSave('supplierId', newSupplier.id, newSupplier.name, newSupplier.documentNumber || undefined);
          setShowCreateSupplier(false);
          setNewSupplierName('');
          setNewSupplierDocument('');
          setDocumentError('');
        },
        onError: (error: Error) => {
          const axiosError = error as AxiosError<{ message?: string }>;
          const message = axiosError.response?.data?.message || 'Erro ao cadastrar fornecedor';
          showNotification(message, 'error');
        },
      }
    );
  };

  const handleRefresh = (key: 'suppliers' | 'expenseCategories' | 'costCenters' | 'financialAccounts') => {
    const keyMap = {
      suppliers: niboKeys.suppliers(customerId),
      expenseCategories: niboKeys.expenseCategories(customerId),
      costCenters: niboKeys.costCenters(customerId),
      financialAccounts: niboKeys.financialAccounts(customerId),
    };
    queryClient.invalidateQueries({ queryKey: keyMap[key] });
  };

  if (!hasIntegration) {
    return (
      <Alert severity="info">
        Conecte ao Nibo para mapear este lançamento.
      </Alert>
    );
  }

  const isLoadingEditData = !isCreateMode && (
    isLoadingEntryData || isLoadingSuppliers || isLoadingCategories || isLoadingCostCenters || isLoadingAccounts
  );

  if (isLoadingEditData) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 2 }}>
        <CircularProgress size={24} />
        <Typography variant="caption" color="text.secondary">
          Carregando dados do Nibo...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
        Dados do Nibo
      </Typography>

      {/* Supplier */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
        <Autocomplete
          fullWidth
          options={suppliers}
          getOptionLabel={(option) => option.name + (option.documentNumber ? ` (${option.documentNumber})` : '')}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              {option.name}{option.documentNumber ? ` (${option.documentNumber})` : ''}
            </li>
          )}
          value={selectedSupplier}
          onChange={(_e, value) => {
            setSelectedSupplier(value);
            handleSave(
              'supplierId',
              value?.id,
              value?.name,
              value?.documentNumber || undefined
            );
          }}
          loading={isLoadingSuppliers}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          slotProps={{ popper: { style: { zIndex: 1500 } } }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Fornecedor"
              size="small"
              error={!!errors?.supplierId}
              helperText={errors?.supplierId}
            />
          )}
          noOptionsText={
            <Button
              startIcon={<AddIcon />}
              onClick={() => setShowCreateSupplier(true)}
              size="small"
              sx={{ textTransform: 'none' }}
            >
              Criar fornecedor
            </Button>
          }
        />
        <Tooltip title="Criar fornecedor">
          <IconButton ref={supplierBtnRef} size="small" onClick={() => setShowCreateSupplier(true)} sx={{ mt: 0.5 }}>
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Atualizar fornecedores">
          <IconButton size="small" onClick={() => handleRefresh('suppliers')} sx={{ mt: 0.5 }}>
            <RefreshIcon fontSize="small" sx={isFetchingSuppliers ? { animation: `${spin} 1s linear infinite` } : undefined} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Category */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
        <Autocomplete
          fullWidth
          options={categories}
          getOptionLabel={(option) => option.name}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>{option.name}</li>
          )}
          value={selectedCategory}
          onChange={(_e, value) => {
            setSelectedCategory(value);
            handleSave('categoryId', value?.id);
          }}
          loading={isLoadingCategories}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          slotProps={{ popper: { style: { zIndex: 1500 } } }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Categoria"
              size="small"
              error={!!errors?.categoryId}
              helperText={errors?.categoryId}
            />
          )}
        />
        <Tooltip title="Atualizar categorias">
          <IconButton size="small" onClick={() => handleRefresh('expenseCategories')} sx={{ mt: 0.5 }}>
            <RefreshIcon fontSize="small" sx={isFetchingCategories ? { animation: `${spin} 1s linear infinite` } : undefined} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Cost Center */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
        <Autocomplete
          fullWidth
          options={costCenters}
          getOptionLabel={formatCostCenterLabel}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>{formatCostCenterLabel(option)}</li>
          )}
          value={selectedCostCenter}
          onChange={(_e, value) => {
            setSelectedCostCenter(value);
            handleSave(
              'costCenterId',
              value?.id,
              undefined,
              undefined,
              value ? getCostCenterSnapshot(value) : undefined
            );
          }}
          loading={isLoadingCostCenters}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          slotProps={{ popper: { style: { zIndex: 1500 } } }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Centro de Custo"
              size="small"
              error={!!errors?.costCenterId}
              helperText={errors?.costCenterId}
            />
          )}
        />
        <Tooltip title="Atualizar centros de custo">
          <IconButton size="small" onClick={() => handleRefresh('costCenters')} sx={{ mt: 0.5 }}>
            <RefreshIcon fontSize="small" sx={isFetchingCostCenters ? { animation: `${spin} 1s linear infinite` } : undefined} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Financial Account */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
        <Autocomplete
          fullWidth
          options={financialAccounts}
          getOptionLabel={(option) => option.name}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>{option.name}</li>
          )}
          value={selectedAccount}
          onChange={(_e, value) => {
            setSelectedAccount(value);
            handleSave('financialAccountId', value?.id);
          }}
          loading={isLoadingAccounts}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          slotProps={{ popper: { style: { zIndex: 1500 } } }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Conta Financeira"
              size="small"
              error={!!errors?.financialAccountId}
              helperText={errors?.financialAccountId}
            />
          )}
        />
        <Tooltip title="Atualizar contas financeiras">
          <IconButton size="small" onClick={() => handleRefresh('financialAccounts')} sx={{ mt: 0.5 }}>
            <RefreshIcon fontSize="small" sx={isFetchingAccounts ? { animation: `${spin} 1s linear infinite` } : undefined} />
          </IconButton>
        </Tooltip>
      </Box>

      {!isCreateMode && upsertMutation.isError && (
        <Alert severity="error">Erro ao salvar dados do Nibo.</Alert>
      )}

      {/* Create Supplier Popover */}
      <Popover
        open={showCreateSupplier}
        anchorEl={supplierBtnRef.current}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        sx={{ zIndex: 1500, pointerEvents: 'none' }}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        hideBackdrop
        slotProps={{
          paper: {
            ref: supplierPopoverRef,
            sx: { width: 320, p: 2, mt: 1, pointerEvents: 'auto' },
          },
        }}
      >
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Novo Fornecedor
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            select
            label="Tipo de Pessoa"
            size="small"
            value={newSupplierType}
            onChange={(e) => setNewSupplierType(e.target.value as 'Jurídica' | 'Física')}
            fullWidth
            SelectProps={{
              MenuProps: {
                disablePortal: true,
                sx: { zIndex: 1600 },
              },
            }}
          >
            <MenuItem value="Física">Física</MenuItem>
            <MenuItem value="Jurídica">Jurídica</MenuItem>
          </TextField>
          <TextField
            label="Nome"
            size="small"
            value={newSupplierName}
            onChange={(e) => setNewSupplierName(e.target.value)}
            fullWidth
          />
          <TextField
            label={newSupplierType === 'Jurídica' ? 'CNPJ' : 'CPF'}
            size="small"
            value={newSupplierDocument}
            onChange={(e) => {
              setNewSupplierDocument(newSupplierType === 'Jurídica' ? formatCNPJ(e.target.value) : e.target.value);
              if (documentError) setDocumentError('');
            }}
            error={!!documentError}
            helperText={documentError}
            slotProps={{ htmlInput: { autoCapitalize: 'characters', maxLength: newSupplierType === 'Jurídica' ? 18 : undefined } }}
            fullWidth
          />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
          <Button onClick={() => setShowCreateSupplier(false)} size="small" color="inherit">
            Cancelar
          </Button>
          <Button
            size="small"
            onClick={handleCreateSupplier}
            disabled={createSupplierMutation.isPending || !newSupplierName || !newSupplierDocument}
          >
            {createSupplierMutation.isPending ? <CircularProgress size={16} /> : 'Criar'}
          </Button>
        </Box>
      </Popover>
    </Box>
  );
};
